"use client";

import cn from "classnames";

import { appendQueryString } from "@/utils/url.util";

import type { HeroSection as HeroSectionType } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

import { Text } from "@/components/text/text.component";

import { RichText } from "../richText/richText.component";
import { HeroSectionCta } from "./heroSectionCta.component";
import { SplitHeroSection } from "./splitHeroSection.component";

export const HeroSection = ({ block, pageName }: HeroSectionProps) => {
  if (block?.layout === "split") {
    return <SplitHeroSection block={block} pageName={pageName} />;
  }

  const backgroundImageUrl = block?.backgroundImage
    ? urlFor(block.backgroundImage).width(1920).height(800).url()
    : null;

  const mobileBackgroundImageUrl = block?.mobileBackgroundImage
    ? urlFor(block.mobileBackgroundImage)
        .width(800)
        .height(1000)
        .fit("max")
        .url()
    : null;

  const isPhoneCta = block?.ctaType === "phone";
  const ctaLabel =
    isPhoneCta && block?.phoneDisplay !== "label" ? null : block?.ctaLabel;
  const ctaHref =
    !isPhoneCta && block?.ctaLink?.url != null
      ? appendQueryString(block.ctaLink.url, block.ctaLink.queryString)
      : null;

  const secondaryCtaHref =
    block?.secondaryCtaLink?.url != null
      ? appendQueryString(
          block.secondaryCtaLink.url,
          block.secondaryCtaLink.queryString,
        )
      : null;

  const hasPrimaryCta = isPhoneCta
    ? Boolean(block?.phoneNumber?.trim())
    : Boolean(ctaLabel && ctaHref);
  const hasSecondaryCta = Boolean(block?.secondaryCtaLabel && secondaryCtaHref);

  return (
    <section
      data-testid="hero-section"
      className="bg-secondary relative flex min-h-[max(21rem,48svh)] w-full flex-col overflow-hidden sm:min-h-[max(22rem,52svh)] md:min-h-[max(24rem,50svh)] lg:min-h-[max(36rem,50vh)]">
      {backgroundImageUrl ? (
        <>
          {mobileBackgroundImageUrl ? (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 z-0 bg-cover bg-center bg-no-repeat lg:hidden"
              style={{
                backgroundImage: `url(${mobileBackgroundImageUrl})`,
              }}
            />
          ) : null}
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-0 z-0 bg-cover bg-center bg-no-repeat lg:bg-right",
              mobileBackgroundImageUrl && "hidden lg:block",
            )}
            style={{
              backgroundImage: `url(${backgroundImageUrl})`,
            }}
          />
        </>
      ) : null}

      {!mobileBackgroundImageUrl ? (
        <div
          className="pointer-events-none absolute inset-0 lg:hidden"
          style={{
            backgroundImage:
              "linear-gradient(to top, rgba(0, 34, 106, 0.95) 0%, rgba(0, 34, 106, 0.3) 60%, rgba(0, 34, 106, 0) 100%)",
          }}
        />
      ) : (
        <div
          className="pointer-events-none absolute inset-0 lg:hidden"
          style={{
            backgroundImage:
              "linear-gradient(to top, rgba(0, 34, 106, 0.95) 0%,  rgba(0, 34, 106, .3) 20%)",
          }}
        />
      )}
      <div
        className="pointer-events-none absolute inset-0 hidden lg:block"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(0, 34, 106, 0.9) 0%, rgba(0, 34, 106, 0) 40%)",
        }}
      />

      <div className="relative container mx-auto flex min-h-0 w-full flex-1 flex-col justify-end px-6 pt-44 pb-10 lg:justify-center lg:pt-16">
        <div className="flex max-w-2xl flex-col lg:mt-10">
          {block?.eyebrow ? (
            <Text
              variant="preHeaderMd"
              component="p"
              data-testid="hero-section-eyebrow"
              className="mb-2 text-white uppercase lg:mb-3">
              {block.eyebrow}
            </Text>
          ) : null}

          <div
            className="mb-4 sm:leading-[1.15]! lg:text-[64px]!"
            data-testid="hero-section-title">
            <RichText asH1={block?.asH1}>{block?.title}</RichText>
          </div>

          {block?.subtitle && (
            <div
              className="font-brown-std mb-8 text-[14px] font-bold lg:mb-6 lg:w-xl lg:pr-14 lg:text-[16px]"
              data-testid="hero-section-subtitle">
              <RichText>{block.subtitle}</RichText>
            </div>
          )}

          {hasPrimaryCta || hasSecondaryCta ? (
            <div
              data-testid="hero-section-ctas"
              className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
              {hasPrimaryCta ? (
                <HeroSectionCta
                  phone={isPhoneCta}
                  phoneNumber={block?.phoneNumber}
                  href={ctaHref}
                  label={ctaLabel}
                  openInNewTab={block?.ctaLink?.openInNewTab}
                  variant="heroPrimary"
                />
              ) : null}
              {hasSecondaryCta ? (
                <HeroSectionCta
                  href={secondaryCtaHref}
                  label={block?.secondaryCtaLabel}
                  openInNewTab={block?.secondaryCtaLink?.openInNewTab}
                  variant="heroSecondary"
                  testId="hero-section-secondary-cta"
                />
              ) : null}
            </div>
          ) : null}

          {block?.legalCallout && (
            <div data-testid="hero-section-legal-callout" className="mt-2">
              <RichText>{block.legalCallout}</RichText>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export type HeroSectionProps = {
  block: HeroSectionType;
  pageName?: string | null;
};
